/**
 * useMainRfpsPageDataV2
 *
 * Custom hook that encapsulates all data fetching and state management logic
 * for RFP list pages. This hook handles:
 * - React Query hooks for fetching submitted and draft RFPs
 * - Session storage for UI state persistence
 * - Draft discard mutation with optimistic updates
 * - Prefetching of opposite tab data
 * - Debounced search functionality
 *
 * @param options - Configuration options for the RFPs page
 * @returns All state and handlers needed to render the RFPs page
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router";
import debounce from "lodash/debounce";
import { useStorage } from "@platform/hooks";
import { SortModelItem } from "ag-grid-community";
import { useQueryClient } from "@tanstack/react-query";
import {
  createServerSideDatasource,
  DataTableProps,
  DEFAULT_PAGE_SIZE,
  FetchPageParams,
  useAgGridOptions,
} from "@platform/design-system";
import { useApi } from "@platform/api";

import { RFPS_SUBMITTED, RFPS_DRAFT } from "../constants";
import { RFP_STATUSES, RfpPageState } from "../constants/rfp";
import { buildRfpSortKey } from "../pages/RFPs/utils";
import { extractErrorMessage } from "../utils";

import { useDiscardRfpDraft } from "./useDiscardRfpDraft";
import {
  fetchRfpsList,
  getRfpsListQueryKey,
  RfpsListResponse,
} from "./useRfpsList";
import { useRfpsTab } from "./useRfpsTab";

const DEFAULT_SUBMITTED_SORT_COL = "details.dueDate";

export interface UseRfpsPageDataOptions {
  brokerId?: string | number;
  clientId?: string | number;
  isBrokerPartner?: boolean;
  isDraftList?: boolean;
  setError?: (value: string | null) => void;
}

export interface UseRfpsPageDataReturn {
  // Data
  submittedCount: number;
  draftCount: number;

  // State
  tabIndex: number;
  sortBy?: string;
  setSortBy?: (sortBy: string) => void;
  isDescending?: boolean;
  setIsDescending?: (isDescending: boolean) => void;
  debouncedQuery: string;
  /** Raw ?q= from the URL, or null when absent. Lets the search box follow a deep link. */
  deepLinkedQuery?: string | null;
  currStatus: string[];
  setCurrStatus: (status: string[]) => void;

  // Handlers
  debouncedSearch: (value: string) => void;
  handleDiscardRfpDraft?: (rfpId: string) => void;
  handleTabChange?: (newTabIndex: number) => void;
  dataTableOptions?: DataTableProps;
}

// eslint-disable-next-line complexity
export function useMainRfpsPageDataV2(
  options: UseRfpsPageDataOptions = {},
): UseRfpsPageDataReturn {
  const {
    brokerId,
    clientId,
    isBrokerPartner = false,
    isDraftList = false,
    setError,
  } = options;
  const [isDataFetching, setIsDataFetching] = useState<boolean>(false);
  const { getStorageItem, setStorageItem } = useStorage("session");

  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  // Session storage & state initialization
  const sessionStorageKey = isBrokerPartner
    ? `brokerRfpPageState-${brokerId}-${isDraftList}`
    : clientId
      ? `clientRfpPageState-${clientId}`
      : "rfpPageState";

  const stored = getStorageItem(sessionStorageKey);
  let rfpPageState: RfpPageState = {};
  if (stored && typeof stored === "object") {
    rfpPageState = stored as RfpPageState;
  }
  // Validate that currStatus contains only valid camelCase keys from RFP_STATUSES
  // This prevents issues if old display values were stored
  const validKeys = Object.keys(RFP_STATUSES);
  const sessionCurrStatus: string[] = (rfpPageState.currStatus || []).filter(
    (status) => validKeys.includes(status),
  );

  const savedSort = rfpPageState.sortModel?.[0];
  const [currStatus, setCurrStatus] = useState<string[]>(
    sessionCurrStatus || [],
  );
  const [sortBy, setSortBy] = useState<string>(
    savedSort?.colId ?? DEFAULT_SUBMITTED_SORT_COL,
  );

  const [isDescending, setIsDescending] = useState<boolean>(
    savedSort ? savedSort.sort === "desc" : true,
  );

  // Refs keep the latest sort state available inside fetchPage without adding
  // them as useCallback dependencies. This prevents the serverSideDatasource
  // from changing on every sort, which would cause AgGrid to re-fetch and race
  // with its own internal SSRM sort-triggered request.
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;
  const isDescendingRef = useRef(isDescending);
  isDescendingRef.current = isDescending;

  // The header search's "See all RFPs" row deep-links here with ?q=, which
  // wins over the query restored from the previous visit. Lower-cased to
  // match what debouncedSearch stores.
  const [searchParams] = useSearchParams();
  const deepLinkedQuery = searchParams.get("q");
  const deepLinkedSearch = deepLinkedQuery?.trim().toLowerCase() ?? "";
  const [debouncedQuery, setDebouncedQuery] = useState<string>(
    deepLinkedSearch || rfpPageState.query || "",
  );

  // "See all RFPs" while already on this page changes ?q= without a remount,
  // so the URL is followed after the first render too.
  useEffect(() => {
    if (deepLinkedQuery !== null) setDebouncedQuery(deepLinkedSearch);
  }, [deepLinkedQuery, deepLinkedSearch]);
  const [submittedCount, setSubmittedCount] = useState<number>(0);
  const [draftCount, setDraftCount] = useState<number>(0);
  const oppositeCountFetchedRef = useRef(false);

  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      const lower = trimmed.toLowerCase();
      if (trimmed.length >= 3 || trimmed.length === 0) setDebouncedQuery(lower);
    }, 300),
  ).current;

  const onSortPersistState = useCallback(
    (sortModel: SortModelItem) => {
      setSortBy(sortModel.colId);
      setIsDescending(sortModel.sort === "desc" || false);
    },
    [setSortBy, setIsDescending],
  );

  // Initial Tab Index
  const { handleTabChange, tabIndex, initialTabIndex } = useRfpsTab({
    setDebouncedQuery,
    setCurrStatus,
    setSortBy,
    setIsDescending,
    rfpPageState,
    isBrokerPartner,
    isDraftList,
    sessionStorageKey,
  });

  const dataTableOptions = useAgGridOptions({
    sessionStorageKey,
    sort: savedSort?.sort ?? "desc",
    colId:
      savedSort?.colId ??
      (initialTabIndex === RFPS_DRAFT
        ? "lastModifyDate"
        : DEFAULT_SUBMITTED_SORT_COL),
    onSortPersistState,
  });

  // Keep tab selection in sync when component is mounted for single-tab partner contexts.
  useEffect(() => {
    if (!isBrokerPartner) return;
    if (isDraftList && tabIndex !== RFPS_DRAFT) handleTabChange(RFPS_DRAFT);
    if (!isDraftList && tabIndex !== RFPS_SUBMITTED)
      handleTabChange(RFPS_SUBMITTED);
  }, [isDraftList, isBrokerPartner, tabIndex, handleTabChange]);

  // Discard draft mutation using shared hook
  const { handleDiscardRfpDraft, isPending: isDiscardPending } =
    useDiscardRfpDraft({
      queryStrategy: "rfp-list",
      setError,
    });

  const fetchPage = useCallback(
    async ({ page, pageSize, sortModel }: FetchPageParams) => {
      try {
        setIsDataFetching(true);
        setError(null);

        const colSort = sortModel?.[0];
        // Prefer the sort model from AgGrid (always present in SSRM requests).
        // Fall back to React state via refs only when sortModel is absent.
        const sortCol = colSort?.colId ?? sortByRef.current;
        const descending = colSort
          ? colSort.sort === "desc"
          : isDescendingRef.current;
        const sortKey = buildRfpSortKey(sortCol, descending);

        const params = {
          rfpState: tabIndex === RFPS_SUBMITTED ? RFPS_SUBMITTED : RFPS_DRAFT,
          page,
          limit: pageSize,
          query: debouncedQuery,
          sortBy: sortCol,
          sortKey,
          statusFilters: tabIndex === RFPS_SUBMITTED ? currStatus : [],
          brokerId,
          clientId,
        };

        const response = await queryClient.fetchQuery({
          queryKey: getRfpsListQueryKey(params),
          queryFn: () => fetchRfpsList(axiosInstance, params),
          staleTime: 5000,
        });

        if (tabIndex === RFPS_SUBMITTED) {
          setSubmittedCount(response.totalResults);
        } else if (tabIndex === RFPS_DRAFT) {
          setDraftCount(response.totalResults);
        }

        // Fetch opposite tab count on initial page load as a reliable fallback.
        // The prefetch effect may race with auth setup or fail silently.
        if (page === 0 && !oppositeCountFetchedRef.current) {
          oppositeCountFetchedRef.current = true;
          const oppositeState =
            tabIndex === RFPS_SUBMITTED ? RFPS_DRAFT : RFPS_SUBMITTED;
          const oppositeSortBy =
            oppositeState === RFPS_SUBMITTED
              ? DEFAULT_SUBMITTED_SORT_COL
              : "lastModifyDate";
          const oppositeParams = {
            rfpState: oppositeState,
            page: 0,
            limit: 1,
            query: "",
            sortBy: oppositeSortBy,
            sortKey: buildRfpSortKey(oppositeSortBy, true),
            statusFilters: oppositeState === RFPS_SUBMITTED ? currStatus : [],
            brokerId,
            clientId,
          };

          fetchRfpsList(axiosInstance, oppositeParams).then((oppositeData) => {
            queryClient.setQueryData(
              getRfpsListQueryKey(oppositeParams),
              oppositeData,
            );
            if (oppositeState === RFPS_SUBMITTED) {
              setSubmittedCount(oppositeData.totalResults || 0);
            } else {
              setDraftCount(oppositeData.totalResults || 0);
            }
          });
        }

        return {
          rows: response.results,
          total: response.totalResults,
        };
      } catch (e: unknown) {
        const error = extractErrorMessage(e) as string;
        setError(error);
      } finally {
        setIsDataFetching(false);
      }
    },
    [
      setError,
      tabIndex,
      debouncedQuery,
      currStatus,
      brokerId,
      clientId,
      queryClient,
      axiosInstance,
    ],
  );

  const serverSideDatasource = useMemo(
    () => createServerSideDatasource(fetchPage),
    [fetchPage],
  );

  // Prefetch opposite tab
  useEffect(() => {
    const oppositeState =
      tabIndex === RFPS_SUBMITTED ? RFPS_DRAFT : RFPS_SUBMITTED;
    const oppositeSortBy =
      oppositeState === RFPS_SUBMITTED
        ? DEFAULT_SUBMITTED_SORT_COL
        : "lastModifyDate";
    const oppositeDescending = true;

    const params = {
      rfpState: oppositeState,
      page: 0,
      limit: 1,
      query: "",
      sortBy: oppositeSortBy,
      sortKey: buildRfpSortKey(oppositeSortBy, oppositeDescending),
      statusFilters: oppositeState === RFPS_SUBMITTED ? currStatus : [],
      brokerId,
      clientId,
      enabled: false,
    } as const;

    const key = getRfpsListQueryKey(params);
    const existing = queryClient.getQueryData<RfpsListResponse>(key);
    if (existing) {
      if (oppositeState === RFPS_SUBMITTED) {
        setSubmittedCount(existing.totalResults || 0);
      } else {
        setDraftCount(existing.totalResults || 0);
      }
      return;
    }

    fetchRfpsList(axiosInstance, params)
      .then((data) => {
        queryClient.setQueryData(key, data);
        if (oppositeState === RFPS_SUBMITTED) {
          setSubmittedCount(data.totalResults || 0);
        } else {
          setDraftCount(data.totalResults || 0);
        }
      })
      .catch(() => {});
  }, [tabIndex, currStatus, brokerId, clientId, queryClient, axiosInstance]);

  const { resetPage, ...restOfDataTableOptions } = dataTableOptions ?? {};

  // Skip resetPage on initial mount — the grid already loads its first block
  // via the server-side datasource. Calling resetPage on mount would purge
  // that in-flight request and trigger an unnecessary second fetch.
  const isInitialMount = useRef(true);

  useEffect(() => {
    const currentState: RfpPageState = (getStorageItem(sessionStorageKey) ||
      {}) as RfpPageState;
    setStorageItem(sessionStorageKey, {
      ...currentState,
      currStatus,
      tabIndex,
      query: debouncedQuery,
    });

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    resetPage?.();
  }, [
    debouncedQuery,
    currStatus,
    tabIndex,
    isDiscardPending,
    resetPage,
    getStorageItem,
    sessionStorageKey,
    setStorageItem,
  ]);

  return {
    // Data
    submittedCount,
    draftCount,

    // State
    tabIndex,
    sortBy,
    setSortBy,
    isDescending,
    setIsDescending,
    debouncedQuery,
    deepLinkedQuery,
    currStatus,
    setCurrStatus,

    // Handlers
    debouncedSearch,
    handleDiscardRfpDraft,
    handleTabChange,
    dataTableOptions: {
      ...restOfDataTableOptions,
      serverSideDatasource,
      loading: isDataFetching || isDiscardPending,
      cacheBlockSize: 20,
      rowModelType: "serverSide",
      serverSideInitialRowCount: DEFAULT_PAGE_SIZE,
    },
  };
}
